/**
 * MfaSection — Profile page "Two-factor auth" section (HoD §3.3, Batch 31a.2.3).
 *
 * Slots into ``frontend/src/pages/admin/Profile.tsx`` between "Edit
 * profile" and "Appearance". Three states drive the TOTP render:
 *
 *   - **Off**  no active credential → "Enable two-factor auth" CTA.
 *   - **On**   active credential → metadata grid + destructive "Disable
 *              two-factor auth" CTA.
 *   - **Mid-flow**  the user clicked Enable; an in-place modal hosts the
 *              shared <MfaEnrollFlow> for the QR + confirm + recovery-
 *              codes round-trip. On step-3 ack, the modal closes and the
 *              section re-renders to "On" (query invalidated by the
 *              enroll-confirm success path).
 *
 * Disable flow (HoD §4.4): a separate confirm modal with password +
 * TOTP fields. The backend re-runs `bcrypt.checkpw` + `pyotp.verify` and
 * (HARD-6) revokes ALL active sessions on success → user is bounced to
 * /admin/login on next request as the apiClient interceptor 401s.
 *
 * WA-1b addition: ``PasskeySection`` card rendered below the TOTP card.
 * Lists enrolled passkeys (nickname / enrolled date / last-used). "Add a
 * passkey" opens an in-place modal with ``PasskeyEnrollFlow``. Each row
 * has a "Remove" button that opens ``PasskeyDeleteModal`` (password-only,
 * no TOTP — F6 divergence). On 204 success all sessions are revoked →
 * bounce to /admin/login.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/common/Button";
import { FormField } from "@/components/common/FormField";
import { OtpCodeInput } from "@/components/auth/OtpCodeInput";
import { MfaEnrollFlow } from "@/components/auth/MfaEnrollFlow";
import { PasskeyEnrollFlow } from "@/components/auth/PasskeyEnrollFlow";
import { Spinner } from "@/components/common/Spinner";
import {
  useAdminMfaCredentials,
  useDisableAdminMfa,
  useEnableAdminMfa,
  useEnableAdminMfaConfirm,
  type AdminMfaCredentialItem,
} from "@/hooks/queries/admin/useAdminMfa";
import {
  useWebauthnCredentials,
  useWebauthnEnrollBegin,
  useWebauthnEnrollFinish,
  useDeleteWebauthnCredential,
  type WebauthnCredentialItem,
} from "@/hooks/queries/admin/useAdminWebauthn";

const CARD =
  "rounded-xl border border-[var(--p-slate-150)] bg-white p-5 shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark";
const INPUT =
  "mt-1 w-full rounded-md border border-[var(--p-slate-150)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary dark:focus:ring-brand-teal";
const LOW_RECOVERY_THRESHOLD = 3;

function formatTimestamp(value: string | null): string {
  if (!value) return "Not yet used";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

// ════════════════════════════════════════════════════════════════════════════
// TOTP section sub-components (unchanged from Batch 31a.2.3)
// ════════════════════════════════════════════════════════════════════════════

function ActiveStateView({
  credential,
  onDisableClick,
}: {
  credential: AdminMfaCredentialItem;
  onDisableClick: () => void;
}): JSX.Element {
  const remaining = credential.recovery_codes_remaining;
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
        <div>
          <dt className="text-accent-primary dark:text-brand-100/50">Status</dt>
          <dd className="text-text-primary dark:text-text-primary">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-brand-positive" aria-hidden />
              Active
            </span>
          </dd>
        </div>
        <div>
          <dt className="text-accent-primary dark:text-brand-100/50">Enrolled</dt>
          <dd className="text-text-primary dark:text-text-primary">
            {formatTimestamp(credential.confirmed_at ?? credential.enrolled_at)}
          </dd>
        </div>
        <div>
          <dt className="text-accent-primary dark:text-brand-100/50">Last used</dt>
          <dd className="text-text-primary dark:text-text-primary">
            {formatTimestamp(credential.last_used_at)}
          </dd>
        </div>
        <div>
          <dt className="text-accent-primary dark:text-brand-100/50">
            Recovery codes left
          </dt>
          <dd className="tabular-nums text-text-primary dark:text-text-primary">
            {remaining} of 10
          </dd>
        </div>
      </dl>

      {remaining < LOW_RECOVERY_THRESHOLD ? (
        <p
          className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
          role="status"
        >
          Only {remaining} recovery {remaining === 1 ? "code" : "codes"} left.
          Contact support to regenerate.
        </p>
      ) : null}

      <div className="flex justify-end">
        <Button variant="danger" onClick={onDisableClick}>
          Disable two-factor auth
        </Button>
      </div>
    </div>
  );
}

function OffStateView({
  onEnableClick,
}: {
  onEnableClick: () => void;
}): JSX.Element {
  return (
    <div className="space-y-3">
      <p className="text-sm text-text-secondary dark:text-brand-100/70">
        Add a one-time code to your sign-in for stronger account security. Off
        by default — enable it for your own account here.
      </p>
      <Button onClick={onEnableClick}>Enable two-factor auth</Button>
    </div>
  );
}

function EnrollModal({
  onClose,
  onComplete,
}: {
  onClose: () => void;
  onComplete: () => void;
}): JSX.Element {
  const enroll = useEnableAdminMfa();
  const confirm = useEnableAdminMfaConfirm();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="mfa-enroll-modal-heading"
    >
      <div className="w-full max-w-lg rounded-xl border border-[var(--p-slate-150)] bg-white p-4 text-text-primary shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:text-text-primary dark:shadow-card-dark">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2
              id="mfa-enroll-modal-heading"
              className="text-base font-semibold text-text-primary dark:text-text-primary"
            >
              Set up two-factor auth
            </h2>
            <p className="mt-1 text-sm text-text-secondary dark:text-brand-100/70">
              Add a one-time code from your authenticator app to your sign-in.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-accent-primary hover:bg-surface-canvas dark:text-brand-100/50 dark:hover:bg-white/[0.04]"
          >
            ×
          </button>
        </div>
        <MfaEnrollFlow
          enrollMutation={enroll}
          confirmMutation={confirm}
          onComplete={onComplete}
          onExpired={onClose}
          continueLabel="Done"
        />
      </div>
    </div>
  );
}

function DisableModal({
  credentialId,
  onClose,
  onComplete,
}: {
  credentialId: string;
  onClose: () => void;
  onComplete: () => void;
}): JSX.Element {
  const disable = useDisableAdminMfa();
  const passwordRef = useRef<HTMLInputElement>(null);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [pwError, setPwError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);

  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    setCodeError(null);
    setGeneralError(null);

    if (!password) {
      setPwError("Enter your current password.");
      return;
    }
    if (code.length !== 6) {
      setCodeError("Enter the 6-digit code from your authenticator.");
      return;
    }

    disable.mutate(
      { credential_id: credentialId, current_password: password, current_totp: code },
      {
        onSuccess: () => {
          onComplete();
        },
        onError: (err) => {
          if (err.error === "invalid_credentials") {
            setPwError("Current password is incorrect.");
          } else if (err.error === "invalid_mfa_code") {
            setCodeError("That code didn't match. Try the next one your app shows.");
            setCode("");
          } else if (err.status === 403) {
            setGeneralError(
              "You can't disable two-factor auth while impersonating another user.",
            );
          } else {
            setGeneralError(
              err.message ||
                "Couldn't disable two-factor auth. Try again in a minute.",
            );
          }
        },
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="mfa-disable-modal-heading"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-xl border border-[var(--p-slate-150)] bg-white p-4 text-text-primary shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:text-text-primary dark:shadow-card-dark"
      >
        <h2
          id="mfa-disable-modal-heading"
          className="text-base font-semibold text-text-primary dark:text-text-primary"
        >
          Disable two-factor auth?
        </h2>
        <p className="mt-1 text-sm text-text-secondary dark:text-brand-100/70">
          Confirm with your password and a current code from your authenticator
          to disable two-factor auth for your account.
        </p>

        <div
          className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2.5"
          role="status"
        >
          <p className="text-xs leading-relaxed text-amber-700 dark:text-amber-300">
            <strong>This signs you out of all sessions.</strong> You'll need to
            sign in again on every device. Your recovery codes will be
            invalidated.
          </p>
        </div>

        <div className="mt-4 space-y-3">
          <FormField
            label="Current password"
            htmlFor="mfa-disable-pw"
            error={pwError ?? undefined}
          >
            <input
              ref={passwordRef}
              id="mfa-disable-pw"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={INPUT}
            />
          </FormField>
          <FormField
            label="Authenticator code"
            htmlFor="mfa-disable-code"
            error={codeError ?? undefined}
          >
            <OtpCodeInput
              id="mfa-disable-code"
              value={code}
              onChange={setCode}
              hasError={codeError !== null}
              disabled={disable.isPending}
            />
          </FormField>
          {generalError ? (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {generalError}
            </p>
          ) : null}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={disable.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            loading={disable.isPending}
          >
            Disable two-factor auth
          </Button>
        </div>
      </form>
    </div>
  );
}

export function MfaSection(): JSX.Element {
  const credentials = useAdminMfaCredentials();
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const active =
    credentials.data?.items.find((item) => item.status === "active") ?? null;

  useEffect(() => {
    if (!toast) return;
    const tid = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(tid);
  }, [toast]);

  return (
    <section className={CARD} aria-labelledby="mfa-heading">
      <h2
        id="mfa-heading"
        className="mb-3 text-base font-semibold text-text-primary dark:text-text-primary"
      >
        Two-factor auth
      </h2>

      {credentials.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-text-secondary dark:text-brand-100/70">
          <Spinner size="sm" /> Loading two-factor status…
        </div>
      ) : credentials.error ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          Couldn't load two-factor status. Refresh to try again.
        </p>
      ) : active ? (
        <ActiveStateView
          credential={active}
          onDisableClick={() => setDisableOpen(true)}
        />
      ) : (
        <OffStateView onEnableClick={() => setEnrollOpen(true)} />
      )}

      {enrollOpen ? (
        <EnrollModal
          onClose={() => setEnrollOpen(false)}
          onComplete={() => {
            setEnrollOpen(false);
            credentials.refetch();
            setToast("Two-factor auth enabled.");
          }}
        />
      ) : null}

      {disableOpen && active ? (
        <DisableModal
          credentialId={active.credential_id}
          onClose={() => setDisableOpen(false)}
          onComplete={() => {
            setDisableOpen(false);
            credentials.refetch();
            setToast("Two-factor auth disabled.");
          }}
        />
      ) : null}

      {toast ? (
        <p
          className="mt-3 text-sm text-[var(--p-green-700)] dark:text-[var(--p-green-350)]"
          role="status"
          aria-live="polite"
        >
          {toast}
        </p>
      ) : null}
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Passkey section (WA-1b)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Password-only delete modal for passkeys (F6 divergence from the TOTP
 * DisableModal). The TOTP modal requires password + authenticator code;
 * this one requires ONLY password because a passkey-only SA has no TOTP
 * code to present. On 204 success, the backend revokes ALL sessions
 * (same ``revoke_user`` call as TOTP delete) → bounce to /admin/login.
 */
function PasskeyDeleteModal({
  credential,
  onClose,
  onRevoked,
}: {
  credential: WebauthnCredentialItem;
  onClose: () => void;
  onRevoked: () => void;
}): JSX.Element {
  const deleteCredential = useDeleteWebauthnCredential();
  const passwordRef = useRef<HTMLInputElement>(null);
  const [password, setPassword] = useState("");
  const [pwError, setPwError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);

  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    setGeneralError(null);

    if (!password) {
      setPwError("Enter your current password.");
      return;
    }

    deleteCredential.mutate(
      { credential_id: credential.id, current_password: password },
      {
        onSuccess: () => {
          // Backend revoked ALL sessions. The apiClient interceptor will 401
          // on the next request; navigate immediately rather than waiting.
          onRevoked();
        },
        onError: (err) => {
          if (err.error === "invalid_credentials") {
            setPwError("Current password is incorrect.");
          } else if (err.status === 404) {
            // Already deleted (race / double-click). Treat as success.
            onRevoked();
          } else {
            setGeneralError(
              err.message ?? "Couldn't remove passkey. Try again in a minute.",
            );
          }
        },
      },
    );
  };

  const displayName = credential.nickname ?? "Passkey";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="passkey-delete-modal-heading"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-xl border border-[var(--p-slate-150)] bg-white p-4 text-text-primary shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:text-text-primary dark:shadow-card-dark"
      >
        <h2
          id="passkey-delete-modal-heading"
          className="text-base font-semibold text-text-primary dark:text-text-primary"
        >
          Remove &ldquo;{displayName}&rdquo;?
        </h2>
        <p className="mt-1 text-sm text-text-secondary dark:text-brand-100/70">
          Confirm with your password to remove this passkey.
        </p>

        <div
          className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2.5"
          role="status"
        >
          <p className="text-xs leading-relaxed text-amber-700 dark:text-amber-300">
            <strong>This signs you out of all sessions.</strong> You'll need to
            sign in again on every device.
          </p>
        </div>

        <div className="mt-4 space-y-3">
          <FormField
            label="Current password"
            htmlFor="passkey-delete-pw"
            error={pwError ?? undefined}
          >
            <input
              ref={passwordRef}
              id="passkey-delete-pw"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={INPUT}
            />
          </FormField>
          {generalError ? (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {generalError}
            </p>
          ) : null}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={deleteCredential.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            loading={deleteCredential.isPending}
          >
            Remove passkey
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Modal hosting PasskeyEnrollFlow (same pattern as EnrollModal above). */
function PasskeyEnrollModal({
  onClose,
  onComplete,
}: {
  onClose: () => void;
  onComplete: () => void;
}): JSX.Element {
  const begin = useWebauthnEnrollBegin();
  const finish = useWebauthnEnrollFinish();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="passkey-enroll-modal-heading"
    >
      <div className="w-full max-w-md rounded-xl border border-[var(--p-slate-150)] bg-white p-4 text-text-primary shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:text-text-primary dark:shadow-card-dark">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2
              id="passkey-enroll-modal-heading"
              className="text-base font-semibold text-text-primary dark:text-text-primary"
            >
              Add a passkey
            </h2>
            <p className="mt-1 text-sm text-text-secondary dark:text-brand-100/70">
              Use Touch ID, Face ID, or a hardware security key to sign in.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-accent-primary hover:bg-surface-canvas dark:text-brand-100/50 dark:hover:bg-white/[0.04]"
          >
            ×
          </button>
        </div>
        <PasskeyEnrollFlow
          beginMutation={begin}
          finishMutation={finish}
          onComplete={onComplete}
        />
      </div>
    </div>
  );
}

/**
 * PasskeySection — "Passkeys" card on the Profile page (WA-1b).
 *
 * Lists the SA's enrolled passkeys (nickname / enrolled date / last-used).
 * "Add a passkey" opens a modal. Each row has "Remove" which opens the
 * password-only delete modal; on 204 → bounce to /admin/login.
 */
export function PasskeySection(): JSX.Element {
  const navigate = useNavigate();
  const credentials = useWebauthnCredentials();
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WebauthnCredentialItem | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const tid = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(tid);
  }, [toast]);

  const items = credentials.data?.items ?? [];

  return (
    <section className={CARD} aria-labelledby="passkey-heading">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2
          id="passkey-heading"
          className="text-base font-semibold text-text-primary dark:text-text-primary"
        >
          Passkeys
        </h2>
        <Button
          onClick={() => setEnrollOpen(true)}
          disabled={credentials.isLoading}
        >
          Add a passkey
        </Button>
      </div>

      <p className="mb-4 text-sm text-text-secondary dark:text-brand-100/70">
        Sign in with Touch ID, Face ID, or a hardware key instead of your
        authenticator app.
      </p>

      {credentials.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-text-secondary dark:text-brand-100/70">
          <Spinner size="sm" /> Loading passkeys…
        </div>
      ) : credentials.error ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          Couldn't load passkeys. Refresh to try again.
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-text-secondary dark:text-brand-100/70">
          No passkeys registered yet.
        </p>
      ) : (
        <ul className="divide-y divide-[var(--p-slate-150)] dark:divide-white/[0.06]">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-start justify-between gap-3 py-3"
            >
              <dl className="min-w-0 flex-1 text-sm">
                <dt className="sr-only">Passkey name</dt>
                <dd className="truncate font-medium text-text-primary dark:text-text-primary">
                  {item.nickname ?? "Unnamed passkey"}
                </dd>
                <dt className="sr-only">Enrolled</dt>
                <dd className="mt-0.5 text-xs text-text-secondary dark:text-brand-100/60">
                  Added {formatTimestamp(item.created_at)}
                </dd>
                <dt className="sr-only">Last used</dt>
                <dd className="text-xs text-text-secondary dark:text-brand-100/60">
                  Last used: {formatTimestamp(item.last_used_at)}
                </dd>
              </dl>
              <Button
                variant="danger"
                onClick={() => setDeleteTarget(item)}
                className="shrink-0"
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      {enrollOpen ? (
        <PasskeyEnrollModal
          onClose={() => setEnrollOpen(false)}
          onComplete={() => {
            setEnrollOpen(false);
            credentials.refetch();
            setToast("Passkey added.");
          }}
        />
      ) : null}

      {deleteTarget ? (
        <PasskeyDeleteModal
          credential={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onRevoked={() => {
            // All sessions revoked — navigate to login immediately.
            setDeleteTarget(null);
            navigate("/admin/login", { replace: true });
          }}
        />
      ) : null}

      {toast ? (
        <p
          className="mt-3 text-sm text-[var(--p-green-700)] dark:text-[var(--p-green-350)]"
          role="status"
          aria-live="polite"
        >
          {toast}
        </p>
      ) : null}
    </section>
  );
}
